<script>
import { FormButton } from "@/components/form";
import LoadingView from "@/components/LoadingView.vue";
import { AttachmentsPane, InfoPane } from "@/components/promotions";
import { LogPane } from "@/components/shared";
import TabPanes from "@/components/TabPanes.vue";
import Promotion from "@/models/promotion";
import { useAuthStore } from "@/stores/auth";
import { handle404 } from '@/helpers/globals';
import { mapState } from "pinia";
import InfoText from "@/components/media/InfoText.vue";
import ModalDialog from "@/components/ModalDialog.vue";
import { GlobalPolicy } from "@/policies/global";
import { PromotionsController } from "@/controllers/promotions";

let tabs = [
    {
        name: 'info',
        prefixIcon: 'fak',
        icon: 'fa-shield',
        title: 'General Information',
        acl: 2
    },
    {
        name: 'attachments',
        prefixIcon: 'fak',
        icon: 'fa-link',
        title: 'Attached Locations',
        acl: 2
    },
    {
        name: 'logs',
        prefixIcon: 'fak',
        icon: 'fa-history',
        title: 'History',
        acl: 2
    }
];

export default {
    setup() {
        return { GlobalPolicy }
    },
    head() {
        return {
            title: () => `${this.$route.meta.title} %separator ${this.promotion?.headline ?? 'Untitled'}`
        }
    },
    data() {
        return {
            promotion: new Promotion({ id: this.$route.params.id }),
            isLoading: true,
            tabs: [],
            prev: false,
            deleting: false
        }
    },
    methods: {
        show() {
            PromotionsController.show(this.promotion.id)
                .then(promotion => {
                    this.promotion = promotion;
                    this.isLoading = false;
                })
                .catch(err => {
                    if(err.response?.status === 404)
                        handle404(this, err.response.data.message);
                    console.error(err);
                });
        },
        loadData() {
            this.show();
        },
        save() {
            this.isLoading = true;
            PromotionsController.update(this.promotion)
                .then(promotion => {
                    this.promotion = promotion;
                    this.isLoading = false;
                })
                .catch(err => {
                    this.isLoading = false;
                    console.error(err);
                });
        },
        prepare(value) {
            this.promotion = new Promotion(value);
            this.save();
        },
        destroy() {
            PromotionsController.delete(this.promotion)
                .then(() => {
                    this.$router.push({ name: 'promotion-list' });
                })
                .catch(err => {
                    this.deleting = false;
                    console.error(err);
                });
        }
    },
    computed: {
        ...mapState(useAuthStore, ['user'])
    },
    created() {
        this.loadData();
        this.tabs = tabs.filter(tab => {
            return this.user.level >= tab.acl
        });
    },
    beforeRouteEnter(to, from, next) {
        next(vm => {
            if(!vm.prev) vm.prev = (from.name && from) ?? false;
        })
    },
    components: {
        LoadingView,
        FormButton,
        TabPanes,
        AttachmentsPane,
        InfoPane,
        LogPane,
        InfoText,
        ModalDialog
    }
}
</script>

<template>
    <LoadingView v-if="isLoading" />
    <div v-else class="flex flex-col gap-8">
        <nav class="flex flex-none gap-4 justify-between items-center rounded-t-lg">
            <div class="flex-none">
                <FormButton
                    color="secondary-bg-white"
                    icon="fa-chevron-left"
                    @click="prev ? $router.push(prev) : $router.go(-1)">
                    Back
                </FormButton>
            </div>
            <div v-if="GlobalPolicy.edit()" class="flex-none">
                <FormButton color="red" prefix-class="fak" icon="fa-trash" @click="deleting = true">Delete</FormButton>
            </div>
        </nav>
        <header class="flex items-start gap-8">
            <div class="flex flex-col sm:flex-row gap-5 sm:gap-6 flex-1">
                <div class="flex flex-col gap-5 sm:gap-8 flex-1 justify-between">
                    <div class="flex flex-col lg:flex-row gap-5 justify-between flex-none">
                        <div class="flex-1 flex flex-col gap-2 lg:gap-0">
                            <h1 class="font-headline antialiased leading-9 text-3xl font-medium bg-gradient-to-r from-bhav-cool-gray-900 to-bhav-cool-gray-600 text-transparent bg-clip-text">
                                {{ promotion.headline }}
                            </h1>
                        </div>
                    </div>
                    <div class="flex flex-col w-full lg:flex-row gap-2">
                        <InfoText label="Type" icon="fak fa-blog" :value="promotion.type" class="w-full" />
                        <InfoText label="Start Date" icon="fak fa-history" :value="promotion.start_at ? promotion.start : null" class="w-full" />
                        <InfoText label="End Date" icon="fak fa-history" :value="promotion.end_at ? promotion.end : null" class="w-full" />
                    </div>
                </div>
            </div>
        </header>
        <TabPanes route="promotion-tab" :tabs="tabs" v-slot="{tab}" class="gap-8">
            <InfoPane :item="promotion" @update="prepare" v-if="tab?.name === 'info'" />
            <AttachmentsPane :id="promotion.id" v-if="tab?.name === 'attachments'" />
            <LogPane context="promotion" v-if="tab?.name === 'logs'" />
        </TabPanes>
    </div>

    <ModalDialog :no-padding="true" :open="deleting" @close="deleting = false">
        <template #header>
            <h2 class="text-lg text-bhav-blue-500 font-semibold">Confirm Promotion Deletion</h2>
        </template>
        <template #body>
            <p class="text-oakwood-gray-900"><strong>Attention!</strong> You are about to delete <em>{{ promotion.headline }}</em>.</p>
            <p class="mt-2"><strong>This will <em>permanently</em> delete this promotion and its images.</strong></p>
            <p class="mt-2">Are you sure?</p>
        </template>
        <template #buttons>
            <FormButton type="button" @click="destroy" color="red">Delete</FormButton>
            <FormButton type="button" @click="deleting = false" color="gray">Cancel</FormButton>
        </template>
    </ModalDialog>
</template>
